import React, { FunctionComponent, useMemo } from "react";
import { components } from "react-select";
import Tooltip from "@mui/material/Tooltip";
import { ReactSelectOption } from "src/lib/globalTypes";
import { Property, SchemaReference, Subject } from "src/lib/graphql/types";
import Dropdown from "src/components/Dropdown";

type SubjectDropdownProps = {
  id?: string;
  dark?: boolean;
  isMulti?: boolean;
  largeList?: boolean;
  onChange: (option: string | string[]) => void;
  options: (Pick<
    Subject | Property,
    "description" | "name" | "namespace" | "version"
  > & { parentChain?: string[] })[];
  placeholder?: string;
  required?: boolean;
  value: string | string[] | undefined | null;
  withVersion?: boolean;
};

export const schemaDropdownValue = (
  { name, namespace, version }: SchemaReference,
  withVersion?: boolean
) => `@${namespace}/${name}` + (version && withVersion ? `/${version}` : "");

const SubjectOptionLabel = (props: any) => (
  <components.Option
    {...props}
    innerRef={(node: HTMLElement) => {
      props?.innerRef?.(node);
    }}
  >
    {props.data.description ? (
      <Tooltip
        arrow
        placement="right"
        classes={{ tooltip: "subject-option-label" }}
        title={props.data.description}
      >
        <div>
          <div>{props.data.label}</div>
          <div className="sub-label">{props.data.subLabel}</div>
        </div>
      </Tooltip>
    ) : (
      <>
        <div>{props.data.label}</div>
        {props.data.subLabel && (
          <div className="sub-label">{props.data.subLabel}</div>
        )}
      </>
    )}
  </components.Option>
);

const SubjectDropdown: FunctionComponent<SubjectDropdownProps> = ({
  dark,
  largeList,
  id,
  isMulti,
  onChange,
  options,
  placeholder,
  required,
  value,
  withVersion,
}) => {
  const subjectOptions: ReactSelectOption[] = useMemo(
    () =>
      options?.map((subject) => ({
        label:
          subject.name === "All" || subject.name === "Create new"
            ? subject.name
            : `${subject.name} @${subject.namespace}`,
        value:
          subject.name === "All" || subject.name === "Create new"
            ? subject.name
            : schemaDropdownValue(subject, withVersion),
        subLabel: subject.parentChain?.[0],
        description: subject.description,
      })) ?? [],
    [options, withVersion]
  );

  let selectedSubjects = isMulti
    ? subjectOptions.filter((opt) =>
        (value as string[])?.find((selection) => selection === opt.value)
      )
    : subjectOptions.find((opt) =>
        value ? opt.value === value : opt.value === "All"
      );

  if (value && !selectedSubjects) {
    selectedSubjects = {
      label: value as string,
      value: value as string,
    };
  }

  return (
    <Dropdown
      value={selectedSubjects}
      className="subject-dropdown"
      id={id || "subjects"}
      options={subjectOptions}
      onChange={(option) =>
        onChange(
          isMulti
            ? (option as ReactSelectOption[])?.map((o) => o.value)
            : (option as ReactSelectOption)?.value
        )
      }
      placeholder={placeholder}
      isClearable={false}
      components={{
        Option: SubjectOptionLabel,
      }}
      isSearchable
      isMulti={isMulti}
      largeList={largeList || options?.length > 200}
      dark={dark}
      required={required}
    />
  );
};

export default SubjectDropdown;
